import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { CheckCircle, XCircle } from '@phosphor-icons/react';

import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

import { cn } from '@/lib/utils';

export enum DiscardReason {
  INCORRECT_ANSWER = 'incorrect_answer',
  HALLUCINATED_ANSWER = 'hallucinated_answer',
  OVERSHARING = 'oversharing',
  WRONG_ARTICLE_SURFACED = 'wrong_article_surfaced',
  OTHER = 'other',
}

export const DiscardReasonLabels: Record<DiscardReason, string> = {
  [DiscardReason.INCORRECT_ANSWER]: 'Incorrect answer',
  [DiscardReason.HALLUCINATED_ANSWER]: 'Hallucinated answer',
  [DiscardReason.OVERSHARING]: 'Oversharing',
  [DiscardReason.WRONG_ARTICLE_SURFACED]: 'Wrong article surfaced',
  [DiscardReason.OTHER]: 'Other',
};

export interface DiscardFormData {
  reason: DiscardReason;
  preferredAnswer?: string;
}

export interface DiscardedMeta {
  reason: DiscardReason;
  suggestedAnswer?: string;
}

export interface SelectOpenHandle {
  isOpen: boolean;
  close: () => void;
}

interface DiscardFormProps {
  discarded?: boolean;
  discardedMeta?: DiscardedMeta;
  onSubmit: (data: DiscardFormData) => Promise<void>;
  onApprove: () => Promise<void>;
  selectOpenRef?: React.MutableRefObject<SelectOpenHandle>;
  className?: string;
}

export function DiscardForm({
  discarded,
  discardedMeta,
  onSubmit,
  onApprove,
  selectOpenRef,
  className,
}: DiscardFormProps): React.ReactNode {
  const [isExpanded, setIsExpanded] = useState(discarded ?? false);
  const [isSelectOpen, setIsSelectOpenState] = useState(false);
  const [isDiscarded, setIsDiscarded] = useState(discarded);
  const formRef = useRef<HTMLFormElement>(null);

  // Sync local state when prop changes (e.g., after server refresh)
  useEffect(() => {
    setIsDiscarded(discarded);
    if (discarded) setIsExpanded(true);
  }, [discarded]);

  const setIsSelectOpen = (open: boolean) => {
    setIsSelectOpenState(open);
    if (selectOpenRef) selectOpenRef.current.isOpen = open;
  };

  // Expose close method to parent via ref - must be in useEffect to avoid side effects during render
  useEffect(() => {
    if (selectOpenRef) {
      selectOpenRef.current.close = () => setIsSelectOpen(false);
    }
  }, [selectOpenRef]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<DiscardFormData>({
    defaultValues: {
      reason: discardedMeta?.reason,
      preferredAnswer: discardedMeta?.suggestedAnswer || '',
    },
  });

  useEffect(() => {
    reset({
      reason: discardedMeta?.reason,
      preferredAnswer: discardedMeta?.suggestedAnswer || '',
    });
  }, [discardedMeta, reset]);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.inert = !isExpanded;
    }
  }, [isExpanded]);

  const handleCancel = () => {
    setIsExpanded(false);
    setIsSelectOpen(false);
    reset();
  };

  const handleApprove = async () => {
    try {
      await onApprove();
      setIsDiscarded(false);
      setIsExpanded(false);
      setIsSelectOpen(false);
    } catch (error) {
      console.error('Failed to approve:', error);
      // TODO: Consider adding error UI feedback
    }
  };

  const handleDiscard = async (data: DiscardFormData) => {
    try {
      await onSubmit(data);
      setIsDiscarded(true);
      setIsSelectOpen(false);
    } catch (error) {
      console.error('Failed to discard:', error);
      // TODO: Consider adding error UI feedback
    }
  };

  const isResolved = isDiscarded !== undefined && isDiscarded !== null;
  const showApproveButton = !isResolved || isDiscarded === false;
  const showDiscardButton = !isResolved || isDiscarded === true;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Buttons */}
      <div className="flex gap-2 px-4">
        {showApproveButton && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="shadowSurface"
                size="sm"
                className={cn('text-default !opacity-100', { 'bg-green-600 text-dark-100': isDiscarded === false })}
                disabled={isResolved}
                onClick={handleApprove}
              >
                <CheckCircle /> {isDiscarded === false ? 'Approved' : 'Approve'}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span>Approve the answer</span>
            </TooltipContent>
          </Tooltip>
        )}
        {showDiscardButton && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="shadowSurface"
                size="sm"
                className={cn('text-default !opacity-100', { 'bg-red-600 text-dark-100': isDiscarded === true })}
                disabled={isResolved}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <XCircle />
                {isDiscarded === true ? 'Discarded' : 'Discard'}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span>Discard the answer</span>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      {/* Form */}
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-in-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        {isDiscarded ? (
          <div
            className={cn('overflow-hidden min-h-0 px-4 bg-dark-200 space-y-4', isExpanded ? 'py-4 mb-4' : 'py-0')}
            data-name="discard-dialog-reason"
          >
            <div className="space-y-1">
              <Label htmlFor="reason" className="text-sm">
                Reason
              </Label>
              <span className="text-sm block">
                {discardedMeta?.reason && DiscardReasonLabels[discardedMeta.reason]}
              </span>
            </div>
            <div className="space-y-1">
              <Label htmlFor="preferredAnswer" className="text-sm">
                Preferred answer
              </Label>
              <span className="text-sm block">{discardedMeta?.suggestedAnswer}</span>
            </div>
          </div>
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit(handleDiscard)}
            className={cn(
              'overflow-hidden min-h-0 px-4 bg-dark-200 transition-all duration-300 ease-in-out',
              isExpanded ? 'py-4 mb-4' : 'py-0',
            )}
          >
            <fieldset className="space-y-4" data-name="discard-dialog-reason">
              <div className="space-y-1">
                <Label htmlFor="reason" className="heading-xs">
                  Reason
                </Label>
                <Controller
                  control={control}
                  name="reason"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      open={isSelectOpen}
                      onOpenChange={setIsSelectOpen}
                      required
                    >
                      <SelectTrigger className="border-dark-300 bg-surface">
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(DiscardReason).map((reason) => (
                          <SelectItem key={reason} value={reason}>
                            {DiscardReasonLabels[reason]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <span className="text-xs text-subtle ml-1 hidden">
                  Give your ambient agent a descriptive name that reflects its purpose. This agent will learn from your
                  content to provide personalized assistance.
                </span>
              </div>
              <div className="space-y-1">
                <Label htmlFor="preferredAnswer" className="heading-xs">
                  Preferred answer
                </Label>
                <Textarea
                  id="preferredAnswer"
                  required
                  minLength={10}
                  placeholder="Explain what you'd like Brainfish to do next time in this case...."
                  rows={4}
                  className="h-52 resize-none bg-surface"
                  {...register('preferredAnswer')}
                />
                <span className="text-xs text-subtle ml-1 hidden">
                  Tell Brainfish why this is the incorrect answer.
                </span>
              </div>
              <div className="flex gap-2 !mb-1 justify-end mr-1">
                <Button variant="link" size="sm" type="button" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="submit" variant="shadow" size="sm" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </fieldset>
          </form>
        )}
      </div>
    </div>
  );
}
